import * as Dialog from "@radix-ui/react-dialog"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState } from "react"
import "./styles.css"

const PARALLAX_FACTOR = 20
const MIN_SLIDE_OPACITY = 0.38

interface GalleryImage {
  src: string
  alt: string
  title: string
  dimensions: { width?: number; height?: number }
}

interface GalleryItem {
  id: string
  coverSrc: string
  coverAlt: string
  coverTitle: string
  coverDimensions: { width?: number; height?: number }
  images: GalleryImage[]
}

interface ScrollGalleryModalProps {
  items: GalleryItem[]
  onOpenChange: (open: boolean) => void
  openIndex: number | null
}

function imageIndexesToLoad(selectedIndex: number, imageCount: number) {
  if (imageCount <= 0) return new Set<number>()

  return new Set([
    selectedIndex,
    (selectedIndex - 1 + imageCount) % imageCount,
    (selectedIndex + 1) % imageCount,
  ])
}

function adjacentImageIndexes(selectedIndex: number, imageCount: number) {
  if (imageCount <= 1) return []

  return [...new Set([(selectedIndex - 1 + imageCount) % imageCount, (selectedIndex + 1) % imageCount])]
}

function WorkGalleryDialogContent({ images }: { images: GalleryImage[] }) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [loadedImageSources, setLoadedImageSources] = useState<Set<string>>(() => new Set())
  const [modalViewportRef, modalEmblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
  })

  const canNavigate = images.length > 1
  const loadableImageIndexes = imageIndexesToLoad(selectedIndex, images.length)

  const syncSelectedIndex = useCallback(() => {
    if (!modalEmblaApi) return
    setSelectedIndex(modalEmblaApi.selectedScrollSnap())
  }, [modalEmblaApi])

  const markImageLoaded = useCallback((src: string) => {
    setLoadedImageSources((currentSources) => {
      if (currentSources.has(src)) return currentSources

      const nextSources = new Set(currentSources)
      nextSources.add(src)
      return nextSources
    })
  }, [])

  const tweenSlides = useCallback(
    (eventName?: "scroll") => {
      if (!modalEmblaApi) return

      const engine = modalEmblaApi.internalEngine()
      const scrollProgress = modalEmblaApi.scrollProgress()
      const scrollSnaps = modalEmblaApi.scrollSnapList()
      const slidesInView = modalEmblaApi.slidesInView()
      const slideNodes = modalEmblaApi.slideNodes()

      scrollSnaps.forEach((scrollSnap, snapIndex) => {
        const slidesInSnap = engine.slideRegistry[snapIndex] ?? [snapIndex]

        slidesInSnap.forEach((slideIndex) => {
          const slide = slideNodes[slideIndex]
          const parallaxLayer = slide?.querySelector<HTMLElement>(".work-gallery-dialog__parallax")

          if (!slide || !parallaxLayer) return
          if (eventName === "scroll" && !slidesInView.includes(slideIndex)) return

          let diffToTarget = scrollSnap - scrollProgress

          if (engine.options.loop) {
            for (const loopItem of engine.slideLooper.loopPoints) {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                diffToTarget =
                  target < 0 ? scrollSnap - (1 + scrollProgress) : scrollSnap + (1 - scrollProgress)
              }
            }
          }

          const tweenValue = diffToTarget * -PARALLAX_FACTOR
          const opacity = Math.max(MIN_SLIDE_OPACITY, 1 - Math.abs(diffToTarget * 2.4))

          slide.style.opacity = String(opacity)
          parallaxLayer.style.transform = `translateX(${tweenValue}%)`
        })
      })
    },
    [modalEmblaApi]
  )

  useEffect(() => {
    if (!modalEmblaApi) return

    const sync = () => {
      syncSelectedIndex()
      tweenSlides()
    }
    const syncScroll = () => {
      tweenSlides("scroll")
    }

    modalEmblaApi.on("scroll", syncScroll)
    modalEmblaApi.on("select", sync)
    modalEmblaApi.on("reInit", sync)
    syncSelectedIndex()
    tweenSlides()

    return () => {
      modalEmblaApi.off("scroll", syncScroll)
      modalEmblaApi.off("select", sync)
      modalEmblaApi.off("reInit", sync)
    }
  }, [modalEmblaApi, syncSelectedIndex, tweenSlides])

  useEffect(() => {
    if (!modalEmblaApi) return

    modalEmblaApi.reInit()
    modalEmblaApi.scrollTo(0, true)
    setSelectedIndex(0)
    tweenSlides()
  }, [modalEmblaApi, tweenSlides])

  useEffect(() => {
    setLoadedImageSources(new Set())
  }, [images])

  useEffect(() => {
    if (typeof Image === "undefined") return

    for (const imageIndex of adjacentImageIndexes(selectedIndex, images.length)) {
      const src = images[imageIndex]?.src
      if (!src || loadedImageSources.has(src)) continue

      const image = new Image()
      image.src = src
    }
  }, [images, loadedImageSources, selectedIndex])

  const scrollPrev = () => {
    if (canNavigate) modalEmblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    if (canNavigate) modalEmblaApi?.scrollNext()
  }

  return (
    <Dialog.Content
      className="work-gallery-dialog__content"
      aria-label="Image gallery"
      aria-describedby={undefined}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") scrollPrev()
        if (event.key === "ArrowRight") scrollNext()
      }}
    >
      <Dialog.Title className="work-gallery-dialog__title">Image gallery</Dialog.Title>
      <Dialog.Close asChild>
        <button
          className="work-gallery-dialog__close work-gallery-dialog__close--content"
          type="button"
          aria-label="Close gallery"
        >
          <i className="fa-sharp fa-regular fa-xmark" />
        </button>
      </Dialog.Close>

      <div
        className="work-gallery-dialog__viewport"
        ref={modalViewportRef}
      >
        <div className="work-gallery-dialog__container">
          {images.map((image, index) => {
            const isLoadable = loadableImageIndexes.has(index)
            const isLoaded = loadedImageSources.has(image.src)

            return (
              <div
                className="work-gallery-dialog__slide"
                data-testid="marketing-gallery-slide"
                key={image.src}
              >
                <figure
                  className="work-gallery-dialog__frame"
                  data-loaded={isLoaded ? "true" : undefined}
                  data-testid="marketing-gallery-frame"
                >
                  <div
                    className="work-gallery-dialog__parallax"
                    data-testid="marketing-gallery-parallax-layer"
                  >
                    <img
                      className={
                        isLoaded ? "work-gallery-dialog__image is-loaded" : "work-gallery-dialog__image"
                      }
                      src={isLoadable || isLoaded ? image.src : undefined}
                      data-src={image.src}
                      alt={image.alt}
                      title={image.title}
                      loading={isLoadable ? "eager" : "lazy"}
                      decoding="async"
                      onLoad={() => markImageLoaded(image.src)}
                      {...image.dimensions}
                    />
                  </div>
                </figure>
              </div>
            )
          })}
        </div>
      </div>

      <div className="work-gallery-dialog__controls">
        <button
          className="work-gallery-dialog__control work-gallery-dialog__control--prev"
          type="button"
          onClick={scrollPrev}
          disabled={!canNavigate}
          aria-label="Previous image"
        >
          <i className="fa-sharp fa-solid fa-chevron-left" />
        </button>

        <div
          className="work-gallery-dialog__status"
          data-testid="marketing-gallery-status"
        >
          {selectedIndex + 1} / {images.length}
        </div>

        <button
          className="work-gallery-dialog__control work-gallery-dialog__control--next"
          type="button"
          onClick={scrollNext}
          disabled={!canNavigate}
          aria-label="Next image"
        >
          <i className="fa-sharp fa-solid fa-chevron-right" />
        </button>
      </div>
    </Dialog.Content>
  )
}

export const ScrollGalleryModal = ({ items, onOpenChange, openIndex }: ScrollGalleryModalProps) => {
  const currentItem = openIndex !== null ? items[openIndex] : null

  return (
    <Dialog.Root
      open={openIndex !== null}
      onOpenChange={onOpenChange}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="work-gallery-dialog__overlay" />
        <Dialog.Close asChild>
          <button
            className="work-gallery-dialog__close work-gallery-dialog__close--viewport"
            type="button"
            aria-label="Close gallery"
          >
            <i className="fa-sharp fa-regular fa-xmark" />
          </button>
        </Dialog.Close>
        {currentItem && <WorkGalleryDialogContent images={currentItem.images} />}
      </Dialog.Portal>
    </Dialog.Root>
  )
}
